import {
  MapLabel,
  MapLabelDecorationStyle,
} from "PosterEditor/model/poster-project";
import { LabelPointerSide, Rect, Point } from "PosterEditor/model/poster-types";

export const drawDecoration = (
  decorationStyle: MapLabelDecorationStyle,
  ctx: CanvasRenderingContext2D,
  rect: Rect,
  side: LabelPointerSide,
  scale: number
) => {
  const { decoration } = decorationStyle;
  if (decoration === "none") {
    return;
  }

  const scaledDecorationStyle: MapLabelDecorationStyle = {
    ...decorationStyle,
    width: decorationStyle.width * scale,
  };

  if (decoration === "line") {
    drawDecoration_Line(scaledDecorationStyle, ctx, rect, side);
  } else if (decoration === "bracket") {
    drawDecoration_Bracket(scaledDecorationStyle, ctx, rect, side);
  } else if (decoration === "border") {
    drawDecoration_Border(scaledDecorationStyle, ctx, rect);
  } else if (decoration === "dot") {
    drawDecoration_Dot(scaledDecorationStyle, ctx, rect, side);
  } else if (decoration === "corner") {
    drawDecoration_Corner(scaledDecorationStyle, ctx, rect, side);
  }
};

export const drawDecoration_Bracket = (
  decorationStyle: MapLabelDecorationStyle,
  ctx: CanvasRenderingContext2D,
  rect: Rect,
  side: LabelPointerSide
) => {
  const { horizontal, vertical } = side;
  const maxWidth = 20;

  let lipSize = { width: 0, height: 0 };
  let leadingLipRect: Rect = { x: 0, y: 0, width: 0, height: 0 };
  let trailingLipRect: Rect = { x: 0, y: 0, width: 0, height: 0 };

  let width = 0;
  let height = 0;
  let x = 0;
  let y = 0;

  if (horizontal === "left" || horizontal === "right") {
    width = decorationStyle.width * maxWidth;
    const maxLength = Math.max(rect.width + width, rect.height);
    height = rect.height;
    lipSize = { width: decorationStyle.length * maxLength, height: width };
  }
  if (vertical === "top" || vertical === "bottom") {
    const maxLength = Math.min(rect.width, rect.height);
    width = rect.width;
    height = decorationStyle.width * maxWidth;
    lipSize = { width: height, height: decorationStyle.length * maxLength };
  }

  if (horizontal === "left") {
    x = rect.x - width;
    y = rect.y;
    leadingLipRect = {
      x: x,
      y: y - lipSize.height,
      width: lipSize.width,
      height: lipSize.height,
    };
    trailingLipRect = {
      x: x,
      y: y + rect.height - lipSize.height + lipSize.height,
      width: lipSize.width,
      height: lipSize.height,
    };
  } else if (horizontal === "right") {
    x = rect.x + rect.width;
    y = rect.y;
    leadingLipRect = {
      x: x - lipSize.width,
      y: y - lipSize.height,
      width: lipSize.width + width,
      height: lipSize.height,
    };
    trailingLipRect = {
      x: x - lipSize.width,
      y: y + rect.height - lipSize.height + lipSize.height,
      width: lipSize.width + width,
      height: lipSize.height,
    };
  }
  if (vertical === "top") {
    y = rect.y - height;
    x = rect.x;
    leadingLipRect = {
      x: x - lipSize.width,
      y: y,
      width: lipSize.width,
      height: lipSize.height + lipSize.width,
    };
    trailingLipRect = {
      x: x + rect.width,
      y: y,
      width: lipSize.width,
      height: lipSize.height + lipSize.width,
    };
  } else if (vertical === "bottom") {
    y = rect.y + rect.height;
    x = rect.x;
    leadingLipRect = {
      x: x - lipSize.width,
      y: y - lipSize.height,
      width: lipSize.width,
      height: lipSize.height + lipSize.width,
    };
    trailingLipRect = {
      x: x + rect.width,
      y: y - lipSize.height,
      width: lipSize.width,
      height: lipSize.height + lipSize.width,
    };
  }

  ctx.fillStyle = decorationStyle.color ?? "red";

  ctx.beginPath();
  ctx.rect(x, y, width, height);
  ctx.fill();

  ctx.beginPath();
  ctx.rect(
    leadingLipRect.x,
    leadingLipRect.y,
    leadingLipRect.width,
    leadingLipRect.height
  );
  ctx.fill();

  ctx.beginPath();
  ctx.rect(
    trailingLipRect.x,
    trailingLipRect.y,
    trailingLipRect.width,
    trailingLipRect.height
  );
  ctx.fill();
};

export const drawDecoration_Line = (
  decorationStyle: MapLabelDecorationStyle,
  ctx: CanvasRenderingContext2D,
  rect: Rect,
  side: LabelPointerSide
) => {
  const { horizontal, vertical } = side;
  const maxWidth = 20;

  let width = 0;
  let height = 0;
  let x = 0;
  let y = 0;
  if (horizontal === "left" || horizontal === "right") {
    width = decorationStyle.width * maxWidth;
    height = rect.height;
  }
  if (vertical === "top" || vertical === "bottom") {
    width = rect.width;
    height = decorationStyle.width * maxWidth;
  }

  if (horizontal === "left") {
    x = rect.x - width;
    y = rect.y;
  } else if (horizontal === "right") {
    x = rect.x + rect.width;
    y = rect.y;
  }
  if (vertical === "top") {
    y = rect.y - height;
    x = rect.x;
  } else if (vertical === "bottom") {
    y = rect.y + rect.height;
    x = rect.x;
  }
  ctx.beginPath();
  ctx.rect(x, y, width, height);
  ctx.fillStyle = decorationStyle.color ?? "black";
  ctx.fill();
};

export const drawDecoration_Border = (
  decorationStyle: MapLabelDecorationStyle,
  ctx: CanvasRenderingContext2D,
  rect: Rect
) => {
  const maxWidth = 20;
  const borderWidth = decorationStyle.width * maxWidth;
  if (borderWidth === 0) {
    return;
  }
  //Draw a border around rect outside
  const x = rect.x - borderWidth / 2;
  const y = rect.y - borderWidth / 2;
  const height = rect.height + borderWidth;
  const width = rect.width + borderWidth;
  ctx.lineWidth = borderWidth;
  ctx.strokeStyle = decorationStyle.color ?? "black";
  ctx.lineJoin = "miter";
  ctx.beginPath();
  ctx.rect(x, y, width, height);
  ctx.stroke();
};

export const drawDecoration_Dot = (
  decorationStyle: MapLabelDecorationStyle,
  ctx: CanvasRenderingContext2D,
  rect: Rect,
  side: LabelPointerSide
) => {
  const { horizontal, vertical } = side;
  const maxWidth = 25;

  let width = decorationStyle.width * maxWidth;
  let x = 0;
  let y = 0;

  if (horizontal === "left") {
    x = rect.x;
    y = rect.y + rect.height / 2;
  } else if (horizontal === "right") {
    x = rect.x + rect.width;
    y = rect.y + rect.height / 2;
  }
  if (vertical === "top") {
    y = rect.y;
    x = rect.x + rect.width / 2;
  } else if (vertical === "bottom") {
    y = rect.y + rect.height;
    x = rect.x + rect.width / 2;
  }
  ctx.beginPath();
  //Draw filled circle
  ctx.arc(x, y, width / 2, 0, 2 * Math.PI);
  ctx.fillStyle = decorationStyle.color ?? "black";
  ctx.fill();
};

export const drawDecoration_Corner = (
  decorationStyle: MapLabelDecorationStyle,
  ctx: CanvasRenderingContext2D,
  rect: Rect,
  side: LabelPointerSide
) => {
  const { horizontal, vertical } = side;
  const maxWidth = 20;
  let thickness = maxWidth * decorationStyle.width;

  let longestSide = Math.max(rect.width, rect.height);

  let length = longestSide * decorationStyle.length + thickness / 2;
  let height = Math.min(rect.height + thickness / 2, length);

  let x = 0;
  let y = 0;
  if (horizontal === "left") {
    x = rect.x - thickness / 2;
  } else if (horizontal === "right") {
    x = rect.x + rect.width + thickness / 2;
    length = -length;
  }
  if (vertical === "top") {
    y = rect.y - thickness / 2;
  } else if (vertical === "bottom") {
    y = rect.y + rect.height + thickness / 2;
    height = -height;
  }
  ctx.strokeStyle = decorationStyle.color ?? "black";
  ctx.lineWidth = thickness;
  ctx.lineCap = "butt";
  ctx.lineJoin = "miter";
  ctx.beginPath();
  ctx.moveTo(x + length, y);

  ctx.lineTo(x, y);
  ctx.lineTo(x, y + height);
  ctx.stroke();
};
